// Turns stored question-bank rows into a paintable quiz body.
//
// The bank stores sanitized HTML; the board is a 2D canvas and has no HTML at all. This module is
// the one place that crossing happens, and it is deliberately pure so node --test can pin it.
// No imports beyond the clock and the content types - no DOMPurify, no React, no network.

import { escapeHtml, markBlanks } from '../../question-bank/viewOnly.ts';
import type { FillBlankAnswer, OpenableViewOnly, ViewBody } from '../../question-bank/viewOnly.ts';
import { QUIZ_ANSWER_MARK, QUIZ_QUESTION_HINT, type QuizBody, type QuizQuestion } from './body.ts';

/** Wall clock shifted to Vietnam, the same +7 the schedule applies. */
function vnDate(now: number): Date {
  return new Date(now + 7 * 60 * 60 * 1000);
}

/** The question-bank fields the painter needs. Narrower than QuestionBankItem on purpose: the
    AI import maps to this shape before the bank stamps an id, so one mapper serves both paths. */
export interface BankQuizQuestion {
  /** Sanitized HTML. */
  content: string;
  /** QuestionBankType, or anything the mapper has not seen - it falls back to trắc nghiệm. */
  type: string;
  options: { content: string; isCorrect: boolean }[];
}

/** Hard caps, so one runaway AI answer cannot bloat the persisted document. The painter also
    ellipsises whatever still does not fit its vertical budget, so nothing clips silently. */
const PROMPT_LIMIT = 240;
const OPTION_LIMIT = 120;
/** A solo page has room for a real passage, a câu chùm stem and two-line statements. */
const PASSAGE_LIMIT = 1200;
const STEM_LIMIT = 700;
const STATEMENT_LIMIT = 260;

const ENTITIES: Record<string, string> = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ',
};

/** Hint line beside `Câu N`, by bank type. */
const TYPE_HINTS: Record<string, string> = {
  'single-choice': QUIZ_QUESTION_HINT,
  'multiple-choice': '(Chọn nhiều đáp án đúng)',
  boolean: '(Chọn Đúng hoặc Sai)',
  text: '(Tự luận)',
};

/**
 * Sanitized HTML to the plain text the canvas can draw.
 *
 * Not a renderer and not a security boundary - the bank already sanitized this. It exists so a
 * `<strong>` or a list marker never reaches the board as literal angle brackets.
 */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  let text = html;
  // KaTeX emits the formula twice, MathML for readers and spans for sight; keeping both would
  // paint the same expression back to back, so the MathML copy goes
  text = text.replace(/<math[\s\S]*?<\/math>/gi, ' ');
  // block edges become spaces, or `A</p><p>B` would paint as `AB`
  text = text.replace(/<\s*(br|\/p|\/li|\/div|\/h[1-6]|\/tr|\/td)[^>]*>/gi, ' ');
  text = text.replace(/<[^>]*>/g, '');
  text = text.replace(/&#x([0-9a-fA-F]+);/g, (_match, code) => String.fromCharCode(parseInt(code, 16)));
  text = text.replace(/&#(\d+);/g, (_match, code) => String.fromCharCode(Number(code)));
  text = text.replace(/&([a-zA-Z]+);/g, (match, name) => ENTITIES[name.toLowerCase()] || match);
  // the painter wraps on single spaces, so every whitespace run has to collapse to exactly one
  return text.replace(/\s+/g, ' ').replace(/^ +/, '').replace(/ +$/, '');
}

function clamp(text: string, limit: number): string {
  if (text.length <= limit) return text;
  return text.slice(0, limit - 1).replace(/ +$/, '') + '…';
}

/**
 * One bank row as a painted question.
 *
 * Tự luận has no options and therefore no key - `correct` is -1 and the reveal stays silent on it
 * rather than marking a row that does not exist. Đúng - Sai has two, nhiều đáp án has several keys,
 * and a set the AI returned with no key at all also lands on -1 instead of marking option A.
 */
export function quizQuestionFromBank(item: BankQuizQuestion): QuizQuestion {
  const essay = item.type === 'text';
  const source = essay ? [] : (item.options || []);
  const options = source.map((option) => clamp(htmlToPlainText(option.content), OPTION_LIMIT) || '(đáp án trống)');
  const keys: number[] = [];
  source.forEach((option, index) => { if (option.isCorrect) keys.push(index); });
  const question: QuizQuestion = {
    prompt: clamp(htmlToPlainText(item.content), PROMPT_LIMIT) || 'Câu hỏi chưa có nội dung.',
    options,
    correct: keys.length > 0 ? keys[0] : -1,
    hint: TYPE_HINTS[item.type] || QUIZ_QUESTION_HINT,
  };
  if (keys.length > 1) question.correctIndexes = keys;
  return question;
}

/** The bank's own difficulty words, so the card and the Kho bài tập row read the same. */
const LEVEL_WORDS: Record<number, string> = { 1: 'Dễ', 2: 'Trung bình', 3: 'Khó' };

/**
 * The one difficulty word a whole set can honestly claim, or undefined when they differ.
 *
 * The AI export applies one difficulty to the whole set, so this normally resolves; a hand-picked
 * selection out of Kho bài tập usually does not, and then the card says nothing rather than
 * averaging three levels into a word no question actually carries.
 */
export function sharedLevelWord(items: { level?: number }[]): string | undefined {
  if (items.length === 0) return undefined;
  const first = items[0].level;
  if (first === undefined || !LEVEL_WORDS[first]) return undefined;
  for (let i = 1; i < items.length; i += 1) {
    if (items[i].level !== first) return undefined;
  }
  return LEVEL_WORDS[first];
}

/**
 * Meta line under the card title, in an `A · B · C` rhythm.
 *
 * DERIVED ONLY FROM WHAT THE EXPORT ACTUALLY CARRIES. `QsExportedData` has `questions`,
 * `chatId`, `createdAt`, `difficulty` and `format` - no subject, no grade, no topic; those exist
 * only in the chat transcript on screen, so no subject or grade is ever guessed here: a teacher
 * generating Toán lớp 6 while the board sits on a Ngữ văn period would be labelled wrong.
 *
 * `data.messages` may hold the transcript, and the topic is in there as Vietnamese prose. Reading
 * it would mean a parser that breaks the first time the model rewords its reply, so it is not read.
 */
export function quizMetaLine(source: string, count: number, level?: string): string {
  const parts = [source, count + ' câu'];
  if (level) parts.push(level);
  return parts.join(' · ');
}

const FILL_BLANK_HINT = '(Điền vào chỗ trống)';
const STATEMENT_HINT = '(Chọn Đúng hoặc Sai cho mỗi ý)';
/** Blank as painted before the reveal. */
const BLANK_GAP = '________';

/** A blank spliced before punctuation leaves `gap .`; the painter wraps on spaces, so close it up. */
function tidy(text: string): string {
  return text.replace(/ ([.,;:!?])/g, '$1');
}

/** Every word of an answer marked, so the painter can colour it; the painter wraps on single spaces. */
function markAnswer(text: string): string {
  return text.split(' ').filter((word) => word !== '').map((word) => QUIZ_ANSWER_MARK + word).join(' ');
}

/**
 * Điền khuyết: blanks painted as `(1) ________`, and filled with their answers on reveal.
 * Answers with no blank of their number in the text (a bare "1+1=?") ride after the passage.
 */
export function fillBlankQuizQuestion(html: string, answers: FillBlankAnswer[] | null, rawCorrect: string): QuizQuestion {
  const blank = markBlanks(html, (number) => ' (' + number + ') ' + BLANK_GAP + ' ');
  const prompt = clamp(tidy(htmlToPlainText(blank.html)), PASSAGE_LIMIT) || 'Câu hỏi chưa có nội dung.';
  const question: QuizQuestion = { prompt, options: [], correct: -1, hint: FILL_BLANK_HINT };
  if (prompt.length > PROMPT_LIMIT) question.solo = true;
  if (answers && answers.length > 0) {
    const byNumber: Record<number, string> = {};
    answers.forEach((answer) => { byNumber[answer.number] = answer.text; });
    const filled = markBlanks(html, (number) => {
      const text = byNumber[number];
      return ' (' + number + ') ' + (text ? escapeHtml(markAnswer(text)) : BLANK_GAP) + ' ';
    });
    const leftover = answers.filter((answer) => filled.numbers.indexOf(answer.number) === -1);
    let reveal = tidy(htmlToPlainText(filled.html));
    if (leftover.length > 0) {
      const list = leftover.length === 1 && filled.numbers.length === 0
        ? markAnswer(leftover[0].text)
        : leftover.map((answer) => '(' + answer.number + ') ' + markAnswer(answer.text)).join('; ');
      reveal += ' Đáp án: ' + list;
    }
    question.revealPrompt = clamp(reveal, PASSAGE_LIMIT + 200);
  } else if (rawCorrect) {
    question.revealPrompt = clamp(prompt + ' Đáp án: ' + markAnswer(rawCorrect), PASSAGE_LIMIT + 200);
  }
  return question;
}

/** Đúng - Sai nhiều ý: the statements are the options, each keyed Đúng or Sai. Always a page alone. */
export function statementQuizQuestion(html: string, statements: { content: string; correct: boolean | null }[]): QuizQuestion {
  return {
    prompt: clamp(htmlToPlainText(html), PASSAGE_LIMIT) || 'Câu hỏi chưa có nội dung.',
    options: statements.map((statement) => clamp(htmlToPlainText(statement.content), STATEMENT_LIMIT) || '(ý trống)'),
    correct: -1,
    hint: STATEMENT_HINT,
    solo: true,
    statementKeys: statements.map((statement) => statement.correct),
  };
}

/** Any answerable body, top-level or a câu chùm child. */
export function quizQuestionFromView(body: ViewBody): QuizQuestion {
  if (body.kind === 'fill-blank') return fillBlankQuizQuestion(body.content, body.answers, body.rawCorrect);
  if (body.kind === 'true-false-choice') return statementQuizQuestion(body.content, body.statements);
  if (body.kind === 'text') return quizQuestionFromBank({ content: body.content, type: 'text', options: [] });
  return quizQuestionFromBank({ content: body.content, type: body.multiple ? 'multiple-choice' : 'single-choice', options: body.options });
}

/**
 * A view-only bank row as painted questions. A câu chùm becomes one page per child, each carrying
 * the shared stem; one whose children never loaded still opens, as its stem alone.
 */
export function quizQuestionsFromViewOnly(item: OpenableViewOnly): QuizQuestion[] {
  if (item.kind === 'fill-blank') return [fillBlankQuizQuestion(item.content, item.answers, item.rawCorrect)];
  if (item.kind === 'true-false-choice') return [statementQuizQuestion(item.content, item.statements)];
  const stem = clamp(htmlToPlainText(item.content), STEM_LIMIT);
  const children = item.children || [];
  if (children.length === 0) {
    return [{
      prompt: stem || 'Câu chùm chưa có nội dung.',
      options: [],
      correct: -1,
      hint: item.children === null ? '(Câu chùm - chưa tải được câu hỏi con)' : '(Câu chùm chưa có câu hỏi con)',
      solo: true,
    }];
  }
  return children.map((child, index) => {
    const question = quizQuestionFromView(child);
    question.solo = true;
    question.part = index + 1;
    if (stem) question.stem = stem;
    return question;
  });
}

/** A row picked out of Kho bài tập: an editable item, or a view-only one (it carries `kind`). */
export type BankQuizEntry = BankQuizQuestion | OpenableViewOnly;

function isViewOnly(entry: BankQuizEntry): entry is OpenableViewOnly {
  return typeof (entry as { kind?: unknown }).kind === 'string';
}

/** Every painted question carries its row number, so a câu chùm's children do not shift the count. */
export function quizBodyFromBank(title: string, meta: string, items: BankQuizEntry[]): QuizBody {
  const questions: QuizQuestion[] = [];
  items.forEach((item, index) => {
    const painted = isViewOnly(item) ? quizQuestionsFromViewOnly(item) : [quizQuestionFromBank(item)];
    painted.forEach((question) => {
      question.number = index + 1;
      questions.push(question);
    });
  });
  return { title, meta, questions };
}

/** `12/09/2026 14:30` in Vietnam time, so the stamp reads the same on any machine clock. */
export function quizStamp(now: number): string {
  const value = vnDate(now);
  const pad = (n: number) => (n < 10 ? '0' + n : String(n));
  return pad(value.getUTCDate()) + '/' + pad(value.getUTCMonth() + 1) + '/' + value.getUTCFullYear()
    + ' ' + pad(value.getUTCHours()) + ':' + pad(value.getUTCMinutes());
}

/** Title for a set the AI Quiz Supporter just produced. Time, not topic - see quizMetaLine. */
export function aiQuizTitle(now: number): string {
  return 'Bài tập trắc nghiệm ' + quizStamp(now);
}

/** The lecture every selected question is attached to, or undefined when they disagree. */
export function sharedLessonName(items: { lessonRef?: { unitName: string } }[]): string | undefined {
  if (items.length === 0) return undefined;
  const first = items[0].lessonRef;
  if (!first || !first.unitName) return undefined;
  for (let i = 1; i < items.length; i += 1) {
    const ref = items[i].lessonRef;
    if (!ref || ref.unitName !== first.unitName) return undefined;
  }
  return first.unitName;
}

/** Title for a set opened out of Kho bài tập - the lecture name when they all share one. */
export function bankQuizTitle(lessonName: string | undefined, now: number): string {
  return lessonName ? 'Câu hỏi: ' + lessonName : 'Câu hỏi từ kho ' + quizStamp(now);
}
